import levenshtein from "fast-levenshtein";
import type { SearchResultItem } from "../types";
import { REPRESENTED_ASSETS_CATEGORY } from "./search-categories";

// // // //

const TOP_RESULTS_LIMIT = 3;

/**
 * Normalize a string for comparison
 */
function normalize(value: string): string {
    return value.trim().toLowerCase();
}

/**
 * Match a field to a query
 */
function matchTier(field: string, query: string): number {
    // A complete match ("XRP Ledger") is the most relevant.
    if (field === query) {
        return 5;
    }
    // A complete leading word ("XRP Ledger") is more relevant than a partial
    // ticker prefix ("XRPTrh") when someone searches for "XRP".
    if (field.startsWith(`${query} `)) {
        return 4;
    }

    // A partial match ("XRP") is more relevant than a complete word ("XRP Ledger").
    return field.startsWith(query) ? 3 : 2;
}

/**
 * Score a search result item
 */
function scoreResult(item: SearchResultItem, query: string) {
    // Asset subtitles contain tickers prefixed with "$" in the search index.
    const fields = [
        normalize(item.content.label),
        normalize(item.content.subtitle ?? "").replace(/^\$/, ""),
    ];

    // Initialize the tier and distance to 0
    let tier = 0;
    let distance = Infinity;

    // Iterate over the fields and score the result
    for (const field of fields) {
        // Skip the field if it doesn't contain the query
        if (!field.includes(query)) {
            continue;
        }

        // Score the field
        const fieldTier = matchTier(field, query);

        // Score the distance
        const fieldDistance = levenshtein.get(field, query);

        // Update the tier and distance if the field is more relevant
        if (
            fieldTier > tier ||
            (fieldTier === tier && fieldDistance < distance)
        ) {
            tier = fieldTier;
            distance = fieldDistance;
        }
    }

    // An issuer/network metadata match must not outrank a name or ticker match.
    const description = normalize(item.content.description ?? "");
    if (tier === 0 && description.includes(query)) {
        tier = 1;
        distance = levenshtein.get(description, query);
    }

    // Missing/invalid values rank after known values, including a genuine zero.
    const totalValue =
        typeof item.totalValue === "number" &&
        Number.isFinite(item.totalValue) &&
        item.totalValue >= 0
            ? item.totalValue
            : -1;

    // Determine the priority of the item
    const priority =
        item.category === REPRESENTED_ASSETS_CATEGORY
            ? 2
            : item.category === "Stocks"
              ? 1
              : 0;

    return { item, tier, distance, totalValue, priority };
}

export function rankSearchResults(
    items: SearchResultItem[],
    query: string,
): SearchResultItem[] {
    const normalizedQuery = normalize(query).replace(/^\$/, "");
    if (normalizedQuery === "") {
        return items;
    }

    // Score once per item, rather than recalculating inside the sort comparator.
    const ranked = items
        .map((item) => scoreResult(item, normalizedQuery))
        .filter(({ tier }) => tier > 0)
        .sort(
            (a, b) =>
                b.tier - a.tier ||
                // Prefer other categories to Stocks for equally strong matches.
                // Represented assets follow Stocks; exact matches still win.
                a.priority - b.priority ||
                a.distance - b.distance,
        );

    // Value only compares assets, not entities with no comparable dollar value.
    // Keep entity positions from text ranking and refill the asset positions in
    // each tier/priority with assets ordered by size. A pairwise "only if both
    // are assets" comparator would be non-transitive when entities are mixed in.
    const assetGroups = new Map<
        string,
        { results: typeof ranked; next: number }
    >();
    for (const result of ranked) {
        // Asset results always carry a number or null; other results omit it.
        if (result.item.totalValue === undefined) continue;
        const key = `${result.tier}:${result.priority}`;
        const group = assetGroups.get(key) ?? { results: [], next: 0 };
        group.results.push(result);
        assetGroups.set(key, group);
    }

    // Sort the results within each group by total value and distance
    assetGroups.forEach((group) => {
        group.results.sort(
            (a, b) => b.totalValue - a.totalValue || a.distance - b.distance,
        );
    });

    // Return the results mapped to the original items
    return ranked.map((result) => {
        if (result.item.totalValue === undefined) return result.item;
        const group = assetGroups.get(`${result.tier}:${result.priority}`)!;
        return group.results[group.next++].item;
    });
}

/** Select distinct, non-represented destinations from the ranked, permission-filtered list. */
export function getTopSearchResults(
    items: SearchResultItem[],
    query: string,
): SearchResultItem[] {
    if (normalize(query).replace(/^\$/, "") === "") {
        return [];
    }

    // Initialize the seen set and top results array
    const seen = new Set<string>();
    const topResults: SearchResultItem[] = [];

    // Iterate over the items and add the top results
    for (const item of items) {
        // Skip the item if it's a represented asset or has already been seen
        if (
            item.category === REPRESENTED_ASSETS_CATEGORY ||
            seen.has(item.href)
        ) {
            continue;
        }

        // Add the item to the seen set and top results array
        seen.add(item.href);
        topResults.push(item);

        // Break if we've reached the top results limit
        if (topResults.length === TOP_RESULTS_LIMIT) {
            break;
        }
    }

    // Return the top results
    return topResults;
}
